import { html, nothing, TemplateResult } from 'lit';
import { keyed } from 'lit/directives/keyed.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

import { Constants } from './product-center-table-wrapper.constants';
import { IconSVG, isQuarterEndDate, sanitizeHTML } from '../../common/utils';
import Locale from '../../common/utils/locale';
import {
  CumulativeDataPoint,
  Disclaimer,
  FundReturnKey,
  Product,
  ProductCenterExhibit,
  SortDirection,
  TableRecordVariant,
  View,
} from './product-center-table-wrapper.model';
import {
  resetTableSortDirectionHeaders,
  sortDataCells,
  sortProductDataCells,
} from '../views/common/helper/table-helper';
import { BeaconTableHeader } from '@trp-beacon-ui-kit/foundation/types/components';
import {
  FilterCategoryField,
  IFilters,
  IProductCenterStore,
  TableTypeEnum,
} from '../store/product-center.store.model';
import productCenterStore from '../store/product-center.store';

export function renderTableHeader(headerData: {
  columnId: string;
  headerName: string;
  showAnnualised?: boolean;
  colspan: number;
  rowspan: number;
  parentHeader: string | null;
  sortable?: boolean;
  customClass?: string;
  infoText?: string;
  onSort?: (e: CustomEvent) => void;
  annualised?: string;
}) {
  return html`
    <th
      scope="col"
      headers="${headerData.parentHeader}"
      colspan="${headerData.colspan}"
      rowspan="${headerData.rowspan}"
      id="${headerData.columnId}-th"
      class="table-header"
    >
      <beacon-table-header
        ?sortable=${headerData.sortable}
        @beacon-event-sort="${headerData.onSort}"
        id="${headerData.columnId}"
      >
        <div class="b-typography-sans-serif--caption--100">
          <div
            class="${headerData.showAnnualised
              ? 'annualised-header-title'
              : 'header-title'} ${headerData.customClass}"
          >
            ${headerData.headerName}
          </div>
          ${headerData.showAnnualised
            ? html` <div>${headerData.annualised}</div> `
            : nothing}
        </div>
        ${headerData.infoText && headerData.infoText !== ''
          ? html`
              <beacon-icon-button
                id="${headerData.columnId}-trigger"
                class="tooltip-icon-button"
                variant="primary"
                slot="action-zone"
              >
                ${IconSVG.info({
                  slot: Constants.ICON_SLOT,
                  ariaLabel: headerData.headerName
                    ? `${headerData.headerName} ${Constants.TOOLTIP}`
                    : undefined,
                })}
              </beacon-icon-button>
              <beacon-popover
                class="table-tooltip"
                placement="bottom-start"
                opens-by="#${headerData.columnId}-trigger"
              >
                <div>${unsafeHTML(sanitizeHTML(headerData.infoText))}</div>
              </beacon-popover>
            `
          : nothing}
      </beacon-table-header>
    </th>
  `;
}

export function renderTableCells(tableCellData: {
  sortValue: string | null | undefined;
  variant: TableRecordVariant;
  cellClass: string;
  value: TemplateResult;
}) {
  return html`
    <td>
      <beacon-table-record
        variant="${tableCellData.variant}"
        sort-value="${tableCellData.sortValue}"
        class="${tableCellData.cellClass}"
      >
        <span class="b-typography-sans-serif--caption--200"
          >${tableCellData.value}</span
        >
      </beacon-table-record>
    </td>
  `;
}

export function renderReportButton(
  reportUrl: string | null | undefined,
  reportAriaLabel?: string | undefined,
  docType?: string,
) {
  const separator = reportUrl && reportUrl.includes('?') ? '&' : '?';
  return html`
    ${reportUrl
      ? html`
          <beacon-icon-button
            variant="tertiary"
            class="report-button"
            link-href="${reportUrl}${separator}inline=true"
            link-target="_blank"
            document-type="${docType ?? ''}"
          >
            ${IconSVG.reportLink({
              slot: Constants.ICON_SLOT,
              ariaLabel: reportAriaLabel ? reportAriaLabel : undefined,
              analytics: {
                dataMetricsLinkText: docType,
                dataMetricsLinkUrl: `${reportUrl}${separator}inline=true`,
                dataMetricsClickable: 'true',
              },
            })}
          </beacon-icon-button>
        `
      : html`-`}
  `;
}

export function renderProductHeader(
  productData: {
    rowspan: number;
    sortByDropdownValue: string;
    sortByCallBack: (e: CustomEvent) => void;
    onSort: (e: CustomEvent) => void;
    tableType: TableTypeEnum;
  },
  localeInstance: Locale,
) {
  return html`
    <th
      scope="col"
      colspan="1"
      id="${Constants.PRODUCT_NAME_COLUMN_ID}"
      rowspan="${productData.rowspan}"
      class="pop-table-header pop-product-header"
    >
      <pop-pc-table-header-component
        data-sort-by="${productData.sortByDropdownValue}"
        table-type="${productData.tableType}"
        .country=${localeInstance.country}
        .language=${localeInstance.language}
        @sort-by-change="${productData.sortByCallBack}"
      ></pop-pc-table-header-component>
      <beacon-table-header
        sortable=""
        @beacon-event-sort="${productData.onSort}"
        id="${Constants.SORT_BUTTON_ID}"
        class="pop-product-header-sort-button"
      >
      </beacon-table-header>
    </th>
  `;
}

export function renderProductCellData(productData: {
  index: string;
  sortValue: string;
  checkboxLabel: string;
  fundName: string;
  shareClass: string;
  currencySymbol: string;
  currencyName: string;
  apir: string;
  productCode: Product['productCode'];
  checkProductCallBack: (e: Event) => void;
  isChecked: boolean;
  fundLink?: string;
  showPopCheckbox?: boolean;
  disclaimers?: Disclaimer[];
  apirLabel?: string;
  noticeLabel?: string;
}) {
  return html`${keyed(
    `${productData.productCode}`,
    html`
      <th>
        <beacon-table-record
          index="${productData.index}"
          sort-value="${productData.sortValue}"
        >
          <beacon-control
            inline
            class="product-checkbox ${!productData.showPopCheckbox
              ? 'hide-checkbox'
              : ''}"
          >
            <input
              class="pop-checkbox"
              type="checkbox"
              name="${productData.productCode}"
              id="${productData.productCode}"
              .checked=${productData.isChecked}
              ?hidden=${!productData.showPopCheckbox}
              @click="${productData.checkProductCallBack}"
              aria-labelledby="product-name"
              aria-describedby="product-desc"
            />
            <label
              id="product-desc"
              for="${productData.productCode}"
              slot="label"
            >
              <span
                class="b-typography-sans-serif--caption--300 b-font-weight-bold "
                >${productData.checkboxLabel}</span
              >
            </label>
          </beacon-control>
          <div
            class="${productData.showPopCheckbox
              ? 'table-record'
              : 'table-record-no-checkbox'}"
          >
            <span
              class="b-typography-sans-serif--caption--200 b-font-weight-bold pop-fund-name"
              ><a
                class="fund-name-link"
                href="${productData.fundLink}"
                target="_blank"
                id="product-name"
                >${productData.fundName}</a
              ></span
            >
            <div
              class="pop-product-information b-typography-sans-serif--caption--200"
            >
              ${productData.shareClass
                ? html`${productData.shareClass} &#183;`
                : nothing}
              ${productData.currencySymbol} ${productData.currencyName}
              ${productData.apir
                ? html` &#183; ${productData.apirLabel}: ${productData.apir}`
                : nothing}
            </div>
            ${productData.disclaimers && productData.disclaimers.length > 0
              ? html`
                  <div class="notice-button">
                    <beacon-button
                      id="notice-disclaimer-${productData.productCode}"
                      class="notice-disclaimer"
                      icon-align="right"
                      variant="tertiary"
                      scale="normal"
                    >
                      <span class="notice-label"
                        >${productData.noticeLabel}</span
                      >
                    </beacon-button>
                  </div>
                  <beacon-popover
                    class="beacon-on-dark notice-popover-container"
                    id="notice-popover-${productData.productCode}"
                    opens-by="#notice-disclaimer-${productData.productCode}"
                    placement="bottom-start"
                  >
                    <beacon-menu></beacon-menu>
                    ${unsafeHTML(
                      sanitizeHTML(
                        productData.disclaimers
                          .map((disclaimer) => disclaimer.disclaimerText.html)
                          .join(''),
                      ),
                    )}
                  </beacon-popover>
                `
              : nothing}
          </div>
        </beacon-table-record>
      </th>
    `,
  )}`;
}

export function getAPCDateFormat(customDateResult: string) {
  const [month, day, year] = customDateResult.replace(',', '').split(' ');
  const paddedDay = day ? day.padStart(2, '0') : '';
  return `${paddedDay} ${month} ${year}`;
}

export function getReportButtonAriaLabel(product: Product, docType: string) {
  return `${docType} ${product.productName.vehicleTypeName} ${product.productName.fundName} ${Constants.DOCUMENT_FORMATS.PDF} ${Constants.DOCUMENT_FORMATS.ARIA_LABEL_DESC}`;
}

// utils.ts
export function handleCommonSortByDropdownChange<T>(
  e: CustomEvent,
  defaultSortOption: T,
  tableHeaders: any,
  requestUpdate: () => void,
): T {
  const value = e.detail?.value;
  const sortByDropdownValue = value ? value : defaultSortOption;
  resetTableSortDirectionHeaders(tableHeaders);
  requestUpdate();
  return sortByDropdownValue;
}

export function handleAdvancedSortByDropdownChange<T>(
  e: CustomEvent,
  defaultSortOption: T,
  tableHeaders: any,
  filteredProducts: any,
  sortButtonId: string,
  shadowRoot: ShadowRoot | null,
  requestUpdate: () => void,
  setCurrentSort: (sort: {
    tableHeaderEl: BeaconTableHeader;
    columnId: string;
  }) => void,
  setFilteredProducts: (products: any) => void,
): T {
  const value = e.detail?.value;
  const sortByDropdownValue = value ? value : defaultSortOption;

  const headerElSorted = shadowRoot?.querySelector(
    Constants.UTILS.BEACON_TABLE_HEADER_SORT_SELECTOR,
  );
  if (headerElSorted && headerElSorted.id) {
    if (headerElSorted.id !== sortButtonId) {
      setCurrentSort({
        tableHeaderEl: headerElSorted as BeaconTableHeader,
        columnId: headerElSorted.id,
      });

      setFilteredProducts(
        sortDataCells(headerElSorted, filteredProducts, headerElSorted.id),
      );
    } else {
      resetTableSortDirectionHeaders(tableHeaders);
    }
  }
  requestUpdate();
  return sortByDropdownValue;
}

export function handleUpdate(
  component: any,
  changedProperties: Map<string, unknown>,
) {
  if (changedProperties.has(Constants.UTILS.PROPERTIES.COMPONENT_OBJECT)) {
    component.filteredProducts =
      (component.componentObject as ProductCenterExhibit).products ?? [];
    component.sortByDropdownValue =
      Constants.DEFAULT_SORT_BY_DROP_DOWN_OPTION.value;
    component.selectedProducts = [];
  }
  if (
    changedProperties.has(Constants.UTILS.PROPERTIES.SORT_BY_DROPDOWN_VALUE) ||
    changedProperties.has(Constants.UTILS.PROPERTIES.SORT_DIRECTION_STATE) ||
    changedProperties.has(Constants.UTILS.PROPERTIES.FILTERED_PRODUCTS)
  ) {
    component.tableHeaders = component.shadowRoot?.querySelectorAll(
      Constants.UTILS.BEACON_TABLE_HEADER_SELECTOR,
    );
  }
}

export function handleSortProductDataCells(component: any, e: CustomEvent) {
  e.preventDefault();
  e.stopImmediatePropagation();
  let tableHeaderEl = e.target as BeaconTableHeader;

  if (!tableHeaderEl) {
    tableHeaderEl = component.shadowRoot?.querySelector(
      `beacon-table-header#${Constants.SORT_BUTTON_ID}`,
    ) as BeaconTableHeader;
    tableHeaderEl.sortDirection = component.sortDirectionState;
  }

  if (tableHeaderEl.sortDirection === SortDirection.ASCENDING) {
    component.sortDirectionState = SortDirection.ASCENDING;
  } else if (tableHeaderEl.sortDirection === SortDirection.DESCENDING) {
    component.sortDirectionState = SortDirection.DESCENDING;
  }

  component.currentSort = {
    tableHeaderEl: tableHeaderEl,
    columnId: component.sortByDropdownValue,
  };

  component.filteredProducts = sortProductDataCells(
    tableHeaderEl,
    component.filteredProducts,
    component.sortByDropdownValue,
  );

  resetTableSortDirectionHeaders(component.tableHeaders, tableHeaderEl);
  component.requestUpdate();
}

export function checkProductCallback(event: Event, tableType: TableTypeEnum) {
  if (event && event.target) {
    const target = event.target as HTMLInputElement;
    if (target.id) {
      const productCenterState: IProductCenterStore =
        productCenterStore.getState();
      productCenterState.toggleCheckProduct(
        tableType,
        target.id,
        target.checked,
      );
    }
  }
}

export function sortDataCellsCallBack(
  component: any,
  e: CustomEvent,
  columnId: string,
) {
  e.preventDefault();
  e.stopImmediatePropagation();
  let tableHeaderEl = e.target as BeaconTableHeader;

  if (!tableHeaderEl) {
    tableHeaderEl = component.shadowRoot?.querySelector(
      `${Constants.UTILS.BEACON_TABLE_HEADER_ID_SELECTOR}${columnId}`,
    ) as BeaconTableHeader;
    tableHeaderEl.sortDirection = component.sortDirectionState;
  }

  if (tableHeaderEl.sortDirection === SortDirection.ASCENDING) {
    component.sortDirectionState = SortDirection.ASCENDING;
  } else if (tableHeaderEl.sortDirection === SortDirection.DESCENDING) {
    component.sortDirectionState = SortDirection.DESCENDING;
  }

  component.currentSort = {
    tableHeaderEl: tableHeaderEl,
    columnId: columnId,
  };

  component.filteredProducts = sortDataCells(
    tableHeaderEl,
    component.filteredProducts,
    columnId,
  );

  resetTableSortDirectionHeaders(component.tableHeaders, tableHeaderEl);
  component.requestUpdate();
}

/**
 * Adds a data-metrics-link-text attribute to beacon-icon-button element.
 *
 * @param {HTMLElement} targetNode - The node to observe for mutations.
 */
export function setAnalyticsAttributesForBeaconIconButton(
  targetNode: HTMLElement,
) {
  const observer = new MutationObserver((mutationsList) => {
    const mutation = mutationsList[0];
    if (mutation.type === 'childList') {
      const beaconIconButtonEl = mutation.target;
      if (
        beaconIconButtonEl instanceof HTMLElement &&
        beaconIconButtonEl.matches('beacon-icon-button.report-button')
      ) {
        const iconEl =
          beaconIconButtonEl.shadowRoot?.querySelector('a.icon-button');
        // Set Analytics Attribute
        if (
          iconEl &&
          !iconEl.getAttribute(Constants.UTILS.ANALYTICS.DATA_METRICS_LINK_TEXT)
        ) {
          iconEl.setAttribute(
            Constants.UTILS.ANALYTICS.DATA_METRICS_LINK_TEXT,
            beaconIconButtonEl.getAttribute('document-type') ?? '',
          );
          observer.disconnect();
        }
      }
    }
  });

  observer.observe(targetNode, { childList: true, subtree: true });
}

/**
 * Sets the width attribute to the beacon popover so that it matches the search field.
 *
 * @param {HTMLElement} popoverEl - The node that will have the resize.
 * @param {HTMLElement} popSearchFieldEl - The node to observe for mutations.
 */
export function observeAndSetBeaconPopoverWidth(
  popoverEl: HTMLElement,
  popSearchFieldEl: HTMLElement,
) {
  const observer = new ResizeObserver(() => {
    popoverEl.style.width = popSearchFieldEl.offsetWidth + 'px';
  });

  observer.observe(popSearchFieldEl);
}

/**
 * Updates the URL with the selected filters.
 * @param {IFilters} filters - The selected filters.
 */
export function updateUrl(filters: IFilters, view: View) {
  //clear current URL params
  const urlParams = new URLSearchParams();
  window.history.pushState({}, '', `${window.location.pathname}`);

  //Add view (Table/Chart) to URLParams
  urlParams.append(Constants.UTILS.URL_PARAMS.VIEW, view);

  //find selected filters
  for (const category of Object.values(FilterCategoryField)) {
    filters[category]?.forEach((filter) => {
      if (filter.isSelected) {
        const filterParam = {
          filterCategory: category,
          filterValue: filter.name,
        };
        //add the selected filter to the URLParams
        urlParams.append(filterParam.filterCategory, filterParam.filterValue);
      }
    });
  }

  //Fire event to bubble filters to the wrapper
  const event = new CustomEvent(Constants.EVENTS.FILTER_UPDATE, {
    detail: { filters },
    bubbles: true,
    composed: true,
  });

  document.dispatchEvent(event);

  //update URL with selected filters
  window.history.pushState(
    {},
    '',
    `${window.location.pathname}?${urlParams.toString()}`,
  );
}

/**
 * Retrieves the latest available data point from the given array of cumulative data points and the fund period.
 *
 * @param dataPoints - The array of cumulative data points.
 * @param fundReturnKey - The period of cumulative data points.
 * @returns The latest quarter-end data point for 10 Year or Since Inception periods, or the last month end data point.
 */
export function getLatestCumulativeDataPoint(
  dataPoints: CumulativeDataPoint[],
  fundReturnKey: FundReturnKey,
): CumulativeDataPoint | null {
  const sortedDataPoints = dataPoints
    .filter((dataPoint) => dataPoint.asOfDate !== null)
    .sort((a, b) => {
      const dateA = new Date(a.asOfDate!);
      const dateB = new Date(b.asOfDate!);
      return dateB.getTime() - dateA.getTime();
    });

  const keysToCheck = new Set([
    FundReturnKey.TEN_YEARS,
    FundReturnKey.SINCE_INCEPTION,
  ]);
  if (keysToCheck.has(fundReturnKey)) {
    for (const dataPoint of sortedDataPoints) {
      if (dataPoint.asOfDate && isQuarterEndDate(dataPoint.asOfDate)) {
        return dataPoint;
      }
    }
    return null;
  }

  return sortedDataPoints[0] ?? null;
}
